<template>
  <q-dialog v-model="visible" full-height position="right">
    <q-card style="width: 1200px; max-width: 95vw">
      <q-toolbar class="auto-height horizontal-line">
        <q-toolbar-title class="q-pa-sm"> {{ modalTitle }} </q-toolbar-title>
        <q-btn flat round dense icon="close" @click="onCancel" />
      </q-toolbar>
      <q-card-section class="row no-wrap dialog-content">
        <!-- Preview Panel -->
        <div class="col-4 q-pr-md preview-container">
          <div class="preview-content">
            <!-- LIFF Form Preview -->
            <FormPreview
              :formFields="form.liff_details || []"
              :formTitle="form.title || 'Dynamic Form'"
              :formImageHeaderUrl="form.image_header_url"
              :readonly="false"
              :showTitle="true"
              :showButtons="false"
              v-if="form.type === 'liff'"
            />

            <!-- Chat Form Preview -->
            <ChatPreview
              :formFields="form.details || []"
              :formTitle="form.title || 'Chat Form'"
              :showTitle="true"
              v-model="chatPreviewData"
              v-if="form.type === 'chat'"
            />
          </div>
        </div>

        <!-- Form Panel -->
        <div class="col-8 q-pl-md form-panel">
          <q-form ref="formRef" greedy class="full-height">
            <div class="form-content">
              <div>
                <label class="text-grey-9 text-bold" v-if="form._id !== ''">{{ $t('label.display_id') }}</label>
                <TagInputContent :tagValue="form.display_id" v-if="form._id !== ''" key="display_id"></TagInputContent>
                <label class="text-grey-9 text-bold">{{ $t('label.form.form_name') }}</label>
                <q-input
                  lazy-rules
                  :rules="[(val) => !isFormInitialized || !!val || requiredRule.formName]"
                  outlined
                  v-model="form.title"
                  dense
                  :placeholder="$t('label.form.form_name')"
                  ref="refTitle"
                >
                </q-input>
              </div>
              <div class="horizontal-line"></div>
              <div class="text-subtitle2 q-mt-sm q-mb-md">フォームタイプ</div>
              <q-btn-group spread>
                <q-btn
                  size="sm"
                  :class="form.type === 'chat' ? 'btn-save' : 'btn-save-grey'"
                  label="チャット形式"
                  @click="form.type = 'chat'"
                />
                <q-btn
                  size="sm"
                  :class="form.type === 'liff' ? 'btn-save' : 'btn-save-grey'"
                  label="フォーム形式"
                  @click="form.type = 'liff'"
                />
              </q-btn-group>
              <div class="horizontal-line q-mt-sm"></div>
              <div
                v-if="form.type === 'liff' && form._id !== '' && selectingApp && selectingApp.liff_channel_id !== ''"
                class="q-mt-md"
              >
                <div>
                  <q-btn
                    no-caps
                    unelevated
                    size="sm"
                    color="grey-6"
                    text-color="white"
                    label="テスト送信"
                    :class="'bg-brand'"
                    @click="handleSendingTestModal()"
                  />
                </div>
                <label class="text-grey-9 text-bold">{{ $t('label.form.liff_url') }}</label>
                <div class="text-subtitle2 q-mt-sm q-mb-md" v-if="form.liff_id && form.liff_id !== ''">
                  <TagInputContent :tagValue="liffUrl" key="liff_id" />
                </div>
                <div v-else>
                  <q-btn
                    size="sm"
                    class="btn-save-green"
                    :label="$t('label.form.activate_liff')"
                    @click="activateLIFF"
                  />
                </div>
              </div>
              <div
                v-if="
                  form.type === 'liff' && form._id !== '' && selectingApp && selectingApp.liff_channel_id_test !== ''
                "
                class="q-mt-md"
              >
                <label class="text-grey-9 text-bold">{{ $t('label.form.liff_url') }} （テストモード）</label>
                <div class="text-subtitle2 q-mt-sm q-mb-md" v-if="form.liff_id_test && form.liff_id_test !== ''">
                  <TagInputContent :tagValue="liffUrlTestMode" key="liff_id_test" />
                </div>
                <div v-else>
                  <q-btn
                    size="sm"
                    class="btn-save-green"
                    :label="$t('label.form.activate_liff')"
                    @click="activateLIFF"
                  />
                </div>
              </div>
              <div class="q-mt-sm">
                <FormDetail
                  v-model="form.details"
                  :formType="form.type"
                  v-if="form.type === 'chat'"
                  ref="detailsForm"
                />
                <FormDetail
                  :modelValue="form.liff_details"
                  @update:modelValue="onFormDetailUpdate"
                  :formType="form.type"
                  :formSize="form.form_size"
                  :formImageHeaderUrl="form.image_header_url"
                  @update:formSize="onUpdateFormSize"
                  @update:formImageHeaderUrl="onUpdateFormImageHeaderUrl"
                  v-else
                  ref="detailsForm"
                />
              </div>
              <q-space class="q-my-md" />
            </div>
            <div class="form-actions">
              <div class="row no-wrap items-center">
                <q-space />
                <div class="q-gutter-sm">
                  <q-btn no-caps flat class="btn-cancel" @click="onCancel">{{ $t('cancel') }}</q-btn>
                  <q-btn no-caps class="btn-save" @click="onSubmit"> {{ $t('save') }} </q-btn>
                </div>
              </div>
            </div>
          </q-form>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
  <SendingTestModal
    v-if="sendingTestModalVisible"
    :modalVisible="sendingTestModalVisible"
    :msg="liffUrl"
    :msgTestMode="liffUrlTestMode"
    :appID="form.app_id"
    :isFlex="true"
    @update:closeModal="onCloseModal"
  />
</template>

<script lang="ts">
import { mixins, Options } from 'vue-class-component'
import BaseFormDialogMixin from '@/components/common/mixins/BaseFormDialogMixin.vue'
import { Prop, Watch } from 'vue-property-decorator'
import { IForm, IInitialCampaign, IPushCampaign, IApp } from '@/utils/types'
import { ACTION_FORM, ACTION_APP } from '@/store/actions'
import cloneDeep from 'lodash/cloneDeep'
import TagInputContent from '@/components/common/ui/TagInputContent.vue'
import InputEmojiSelector from '@/components/common/ui/InputEmojiSelector.vue'
import FormDetail from '@/components/form/FormDetail.vue'
import { ASSETS_FORM_TYPE } from '@/utils/constants'
import SendingTestModal from '@/components/common/ui/SendingTestModal.vue'
import FormPreview from '@/components/common/ui/FormPreview.vue'
import ChatPreview from '@/components/common/ui/ChatPreview.vue'

@Options({
  components: { FormDetail, InputEmojiSelector, TagInputContent, SendingTestModal, FormPreview, ChatPreview },
  emits: ['update:onSelect', 'update:onCreate'],
})
export default class FormFormModal extends mixins(BaseFormDialogMixin) {
  @Prop()
  formConfig!: IForm

  @Prop()
  campaign!: IInitialCampaign | IPushCampaign

  @Prop({ default: '' })
  appId!: string

  form: IForm = {
    _id: '',
    title: '',
    app_id: this.appId,
    message: '',
    details: [],
    liff_details: [],
    liff_id: '',
    applied_campain: 0,
    type: ASSETS_FORM_TYPE.CHAT,
    form_size: 'compact',
    liff_id_test: '',
  }

  selectingApp: IApp = {}
  sendingTestModalVisible = false
  chatPreviewData: Record<string, any> = {}
  isFormInitialized = false
  isUpdatingFromFormDetail = false

  get requiredRule() {
    return {
      formName: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.form.form_name'),
      }),
    }
  }

  get liffUrl() {
    return this.form.liff_id ? `https://liff.line.me/${this.form.liff_id}` : ''
  }

  get liffUrlTestMode() {
    return this.form.liff_id_test ? `https://liff.line.me/${this.form.liff_id_test}` : ''
  }

  get modalTitle() {
    return this.isNew ? this.$t('label.form.add_new_form') : this.$t('label.form.edit_form')
  }

  handleSendingTestModal() {
    this.sendingTestModalVisible = true
  }

  onCloseModal() {
    this.sendingTestModalVisible = false
  }

  onUpdateFormSize(value) {
    this.form.form_size = value
  }

  onUpdateFormImageHeaderUrl(value) {
    this.form.image_header_url = value
  }

  onFormDetailUpdate(newData: any[]) {
    this.isUpdatingFromFormDetail = true
    this.form.liff_details = [...newData]
    this.$nextTick(() => {
      this.isUpdatingFromFormDetail = false
    })
  }

  @Watch('form.details', { deep: true })
  onDetailsChange() {
    this.chatPreviewData = this.initializeChatPreviewData(this.chatPreviewData)
  }

  initializeChatPreviewData(existingData: Record<string, any> = {}) {
    if (!this.form.details || this.form.details.length === 0) {
      return {}
    }

    // Initialize chat preview data, preserving existing
    return this.form.details.reduce((data, field) => {
      if (field.field_name) {
        if (existingData[field.field_name] !== undefined) {
          data[field.field_name] = existingData[field.field_name]
        } else {
          data[field.field_name] = ''
        }
      }
      return data
    }, {} as Record<string, any>)
  }

  onSubmit() {
    this.$refs.formRef
      .validate()
      .then(async (success: boolean) => {
        if (!success) {
          return
        }

        if (
          this.form.type === ASSETS_FORM_TYPE.LIFF &&
          (this.selectingApp.liff_channel_id === '' || this.selectingApp.liff_channel_secret === '')
        ) {
          this.$q.notify({
            type: 'negative',
            message: this.$t('messages.please_set_liff_channel_id_in_app_settings'),
          })
          return
        }

        await this.doSave()
      })
      .catch((error: unknown) => {
        console.log('error', error)
      })
  }

  async doSave() {
    const { dispatch } = this.$store
    let form = null
    const isNew = this.isNew
    if (isNew) {
      form = await dispatch(ACTION_FORM.ADD_NEW, {
        ...this.form,
      })
    } else {
      form = await dispatch(ACTION_FORM.UPDATE, {
        _id: this.formConfig._id,
        ...this.form,
      })
    }

    if (form) {
      if (isNew) {
        this.$emit('update:onCreate', form)
      } else {
        this.$emit('update:onSelect', form)
      }
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })
    }

    this.visible = false
  }

  mounted() {
    this.$nextTick(() => {
      this.$refs.formRef.validate()
      this.$refs.refTitle.focus()
    })
  }

  async activateLIFF() {
    if (!this.formConfig._id) {
      return
    }

    const { dispatch } = this.$store
    const form = await dispatch(ACTION_FORM.ACTIVATE_LIFF, {
      _id: this.formConfig._id,
      ...this.form,
    })
    if (form) {
      this.$emit('update:onSelect', form)
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })
    }
    this.visible = false
  }

  async created() {
    this.selectingApp = await this.$store.dispatch(ACTION_APP.LOAD_ITEM, this.formConfig.app_id)
    this.form = { ...this.form, ...cloneDeep(this.formConfig) }
    this.parentForm = this.form as any
    this.initForm = cloneDeep(this.parentForm)
    this.chatPreviewData = this.initializeChatPreviewData()

    // Set initialization flag after form is populated
    this.$nextTick(() => {
      this.isFormInitialized = true
    })
  }
}
</script>

<style scoped lang="scss">
.dialog-content {
  height: calc(100% - 50px);
  min-height: 500px;
  max-height: 100%;
  overflow: hidden;
  padding: 0 !important;
}

.preview-container {
  padding: 16px;
  background-color: #9e9e9e33;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
}

.form-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
}

.full-height {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.form-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-right: 8px;
}

.form-actions {
  flex-shrink: 0;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
  margin-top: 16px;
}
</style>
